import { useEffect } from "react";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "./ui/button";

interface OidcRedirectProps {
  providerName: string;
  redirectUrl: string;
  onBack: () => void;
}

export function OidcRedirect({
  providerName,
  redirectUrl,
  onBack,
}: OidcRedirectProps) {
  useEffect(() => {
    const timer = setTimeout(() => {
      try {
        // Resolve against the current origin. The server hands back a RELATIVE
        // url ("/api/auth/authorize?email=…") and single-argument `new URL()`
        // throws on a relative input — which sent this straight to the catch
        // below, so the component logged and sat on "Redirecting…" forever
        // while the browser never moved. The backend was returning a correct
        // 302 to Microsoft the whole time; nothing ever asked it for one.
        const parsed = new URL(redirectUrl, window.location.origin);

        // Tell the server where to put the user back. Without this the callback
        // always lands on the console root, so signing in from /workbench/
        // deposited you in the console — the two apps share one host, one
        // server and one callback, and nothing carried which of them started
        // the flow.
        //
        // Derived from the app's own base rather than passed in by each caller,
        // so a new app mounted at another path gets this right without
        // remembering to. Deliberately the BASE, not the current path: at
        // /workbench/login the current path is the login screen, and returning
        // there post-login would just show it again.
        const base = import.meta.env.BASE_URL || "/";
        if (!parsed.searchParams.has("return_to")) {
          parsed.searchParams.set("return_to", base);
        }

        // Same-origin only. This navigation must land on OUR /api/auth/authorize,
        // which then 302s to the identity provider — the cross-origin hop is the
        // server's to make, never ours. Checking the protocol alone (as this did)
        // accepts https://anywhere, so a manipulated lookup response could send a
        // user mid-login to an attacker-controlled page that looks like the IdP.
        // An open redirect on a login screen is about the most useful one there is.
        if (parsed.origin !== window.location.origin) {
          console.error("Cross-origin OIDC redirect blocked:", redirectUrl);
          return;
        }
        if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
          console.error("Unsafe OIDC redirect URL blocked:", redirectUrl);
          return;
        }

        window.location.href = parsed.toString();
      } catch {
        console.error("Invalid OIDC redirect URL:", redirectUrl);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [redirectUrl]);

  return (
    <div className="space-y-6 text-center">
      <p className="text-lg font-medium text-gray-900 dark:text-white">
        Redirecting to {providerName}...
      </p>

      <div className="flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-coronis-blue-600" />
      </div>

      <Button
        type="button"
        variant="link"
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mx-auto"
      >
        <ArrowLeft className="h-3 w-3" />
        Use a different account
      </Button>
    </div>
  );
}
